// Pure chunk-grid math for the renderer's chunked terrain cache. No canvas /
// DOM dependencies so it is unit-testable headlessly (chunkGrid.test.ts).
//
// The 256×256 tile map is divided into a CHUNKS_PER_SIDE × CHUNKS_PER_SIDE
// grid of CHUNK_TILES × CHUNK_TILES-tile chunks. Each chunk owns a lazily
// allocated offscreen canvas painted at source-art scale (16 px/tile →
// 1024×1024 px ≈ 4 MB of backing store). Chunk indices are row-major:
// index = chunkY * CHUNKS_PER_SIDE + chunkX.

import { MAP_SIZE } from '@bolo2k/shared';

/** Tiles per chunk side. 64 tiles × 16 px source art = 1024 px chunk canvas. */
export const CHUNK_TILES = 64;
export const CHUNKS_PER_SIDE = MAP_SIZE / CHUNK_TILES; // 4
export const CHUNK_COUNT = CHUNKS_PER_SIDE * CHUNKS_PER_SIDE; // 16

/** Residency cap for the chunk LRU. A viewport typically intersects ≤4
 *  chunks (≤9 on very large windows), so 12 keeps every visible chunk plus
 *  a ring of recently-seen neighbours resident while bounding worst-case
 *  canvas memory at 12 × 4 MB ≈ 50 MB. */
export const MAX_RESIDENT_CHUNKS = 12;

/** Chunk index owning tile (tileX, tileY). Caller guarantees in-map coords. */
export function chunkOfTile(tileX: number, tileY: number): number {
  return ((tileY / CHUNK_TILES) | 0) * CHUNKS_PER_SIDE + ((tileX / CHUNK_TILES) | 0);
}

/** Top-left tile coordinate of a chunk. */
export function chunkOriginTiles(chunkIndex: number): { tileX: number; tileY: number } {
  return {
    tileX: (chunkIndex % CHUNKS_PER_SIDE) * CHUNK_TILES,
    tileY: ((chunkIndex / CHUNKS_PER_SIDE) | 0) * CHUNK_TILES,
  };
}

/** Visit the 3×3 neighbourhood of a tile (given as a row-major tile index),
 *  clamped to the map — no wrap-around at the edges. The neighbour-aware
 *  tile picker means a terrain change at (x, y) can change the *visual* of
 *  all 8 surrounding tiles, so a dirty tile always repaints this patch. */
export function forEachNeighborTile(
  tileIndex: number,
  visit: (x: number, y: number) => void,
): void {
  const x = tileIndex % MAP_SIZE;
  const y = (tileIndex / MAP_SIZE) | 0;
  for (let dy = -1; dy <= 1; dy++) {
    const ny = y + dy;
    if (ny < 0 || ny >= MAP_SIZE) continue;
    for (let dx = -1; dx <= 1; dx++) {
      const nx = x + dx;
      if (nx < 0 || nx >= MAP_SIZE) continue;
      visit(nx, ny);
    }
  }
}

/** Chunks whose cached pixels a change at `tileIndex` can affect — the
 *  owning chunk plus any chunk the clamped 3×3 neighbourhood spills into
 *  (1, 2, or 4 chunks depending on proximity to chunk borders). */
export function affectedChunksForTile(tileIndex: number): number[] {
  const out: number[] = [];
  forEachNeighborTile(tileIndex, (x, y) => {
    const c = chunkOfTile(x, y);
    if (!out.includes(c)) out.push(c);
  });
  return out;
}

/** Tiny LRU over chunk indices. The renderer touches every chunk it blits
 *  each frame; when residency exceeds capacity the least-recently-touched
 *  chunks are reported for eviction (the renderer drops their canvases and
 *  repaints on next visibility). */
export class ChunkLRU {
  private order: number[] = []; // least-recently-used first

  constructor(private readonly capacity: number) {
    if (capacity < 1) throw new Error('ChunkLRU capacity must be >= 1');
  }

  /** Mark `key` most-recently used (inserting if new). Returns the keys
   *  evicted to stay within capacity — never includes `key` itself. */
  touch(key: number): number[] {
    const i = this.order.indexOf(key);
    if (i >= 0) this.order.splice(i, 1);
    this.order.push(key);
    const evicted: number[] = [];
    while (this.order.length > this.capacity) evicted.push(this.order.shift()!);
    return evicted;
  }

  has(key: number): boolean {
    return this.order.includes(key);
  }

  get size(): number {
    return this.order.length;
  }

  clear(): void {
    this.order.length = 0;
  }
}
